import React, { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { fetchMessages, markMessagesRead } from '../../store/slices/chatSlice';
import socketService from '../../utils/socketService';
import { PaperAirplaneIcon, CheckIcon, InformationCircleIcon } from '@heroicons/react/24/solid';
import { CheckIcon as CheckOutlineIcon } from '@heroicons/react/24/outline';

const ChatWindow = () => {
    const { activeRoom, activeRoomMessages, isLoading } = useSelector((state) => state.chat);
    const { user } = useSelector((state) => state.auth);
    const dispatch = useDispatch();
    const [newMessage, setNewMessage] = useState('');
    const messagesEndRef = useRef(null);
    const [messageDetail, setMessageDetail] = useState(null);
    const [typingUser, setTypingUser] = useState(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        if (activeRoom) {
            dispatch(fetchMessages({ roomId: activeRoom.id }));
        }
    }, [activeRoom, dispatch]);

    // Typing indicators - use named functions for proper cleanup
    useEffect(() => {
        if (!activeRoom) return;

        const handleTyping = (data) => {
            if (data.userId !== user.id) {
                setTypingUser(data.name || 'Someone');
            }
        };

        const handleStopTyping = (data) => {
            if (data.userId !== user.id) {
                setTypingUser(null);
            }
        };

        socketService.on('user_typing', handleTyping);
        socketService.on('user_stop_typing', handleStopTyping);

        return () => {
            socketService.off('user_typing', handleTyping);
            socketService.off('user_stop_typing', handleStopTyping);
            setTypingUser(null); // Clear typing indicator on unmount
        };
    }, [activeRoom, user.id]);

    useEffect(() => {
        scrollToBottom();

        // Auto-mark read if new message arrives while window is open
        if (activeRoomMessages.length > 0 && activeRoom) {
            const lastMsg = activeRoomMessages[activeRoomMessages.length - 1];
            if (lastMsg.senderId !== user.id && !lastMsg.isRead) {
                // Optimization: Only dispatch if not already processing to avoid spam? 
                // The backend update is cheap enough.
                dispatch(markMessagesRead(activeRoom.id));
                socketService.emit('mark_messages_read', { roomId: activeRoom.id });
            }
        }
    }, [activeRoomMessages, activeRoom, user.id, dispatch]);

    const handleSend = (e) => {
        e.preventDefault();
        if (!newMessage.trim() || !activeRoom) return;

        socketService.sendMessage(activeRoom.id, newMessage);
        setNewMessage('');
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            handleSend(e);
        }
    };

    if (!activeRoom) return null;

    return (
        <div className="flex flex-col h-full bg-slate-50 relative">
            {/* Message Detail Popup */}
            {messageDetail && (
                <div
                    className="absolute inset-0 z-50 flex items-center justify-center bg-black/20 backdrop-blur-sm p-4 animate-fadeIn"
                    onClick={() => setMessageDetail(null)}
                >
                    <div
                        className="bg-white rounded-xl shadow-xl border border-gray-100 p-4 w-full max-w-[250px] space-y-3 transform transition-all scale-100"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex justify-between items-center border-b border-gray-100 pb-2">
                            <h4 className="font-bold text-gray-700 text-sm">Message Info</h4>
                            <button
                                onClick={() => setMessageDetail(null)}
                                className="p-1 hover:bg-gray-100 rounded-full"
                            >
                                <CheckOutlineIcon className="w-4 h-4 text-gray-400" />
                            </button>
                        </div>
                        <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                                <span className="text-gray-500">Sent:</span>
                                <span className="font-medium text-gray-800">
                                    {new Date(messageDetail.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-500">Date:</span>
                                <span className="font-medium text-gray-800">
                                    {new Date(messageDetail.createdAt).toLocaleDateString()}
                                </span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-gray-500">Status:</span>
                                <span className={`font-bold px-2 py-0.5 rounded-full text-xs ${messageDetail.isRead ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                                    {messageDetail.isRead ? 'Read' : 'Delivered'}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Messages Area */}
            <div className="flex-1 overflow-y-auto px-6 pt-4 pb-2 space-y-4 custom-scrollbar">
                {isLoading && activeRoomMessages.length === 0 && (
                    <div className="text-center text-xs text-gray-400 mt-4">Loading messages...</div>
                )}

                {activeRoomMessages.map((msg, idx) => {
                    const isMe = msg.senderId === user.id;
                    const isRead = msg.isRead;

                    return (
                        <div key={msg.id || idx} className={`flex ${isMe ? 'justify-end' : 'justify-start'} items-end gap-2 group relative mb-2`}>
                            {/* Info Icon for Sent Messages (Left of bubble) */}
                            {isMe && (
                                <button
                                    className="opacity-0 group-hover:opacity-100 transition-opacity p-1 text-gray-400 hover:text-blue-600 bg-white rounded-full shadow-sm border border-gray-100 shrink-0 mb-1"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setMessageDetail(msg);
                                    }}
                                    title="Message Info"
                                >
                                    <InformationCircleIcon className="w-4 h-4" />
                                </button>
                            )}

                            {/* Message Bubble */}
                            <div
                                className={`max-w-[75%] px-4 py-2 rounded-2xl text-sm shadow-sm relative ${isMe
                                    ? 'bg-blue-600 text-white rounded-br-none'
                                    : 'bg-white text-gray-800 border border-gray-100 rounded-bl-none'
                                    }`}
                            >
                                <p className="pr-4">{msg.message}</p>
                                <div className={`text-[10px] mt-1 flex justify-end items-center gap-1 ${isMe ? 'text-blue-100' : 'text-gray-400'}`}>
                                    <span>
                                        {new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                    </span>
                                    {isMe && (
                                        <span title={isRead ? "Read" : "Sent"}>
                                            {isRead ? (
                                                <div className="flex -space-x-1">
                                                    <CheckIcon className="w-3 h-3 text-blue-200" />
                                                    <CheckIcon className="w-3 h-3 text-blue-200" />
                                                </div>
                                            ) : (
                                                <CheckIcon className="w-3 h-3 text-blue-200 opacity-70" />
                                            )}
                                        </span>
                                    )}
                                </div>
                            </div>

                            {/* Info Icon for Received Messages (Right of bubble) */}
                            {!isMe && (
                                <button
                                    className="opacity-0 group-hover:opacity-100 transition-opacity p-1 text-gray-400 hover:text-blue-600 bg-white rounded-full shadow-sm border border-gray-100 shrink-0 mb-1"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setMessageDetail(msg);
                                    }}
                                    title="Message Info"
                                >
                                    <InformationCircleIcon className="w-4 h-4" />
                                </button>
                            )}
                        </div>
                    );
                })}
                <div ref={messagesEndRef} />
            </div>

            {/* Typing Indicator Overlay */}
            {typingUser && (
                <div className="px-4 py-2 bg-transparent">
                    <div className="text-xs text-gray-500 italic animate-pulse">
                        {typingUser} is typing...
                    </div>
                </div>
            )}

            {/* Input Area */}
            <div className="p-3 bg-white border-t border-gray-100 shrink-0">
                <form onSubmit={handleSend} className="relative flex items-center gap-2">
                    <input
                        type="text"
                        value={newMessage}
                        onChange={(e) => {
                            setNewMessage(e.target.value);
                            socketService.typing(activeRoom.id);

                            if (window.typingTimeout) clearTimeout(window.typingTimeout);
                            window.typingTimeout = setTimeout(() => {
                                socketService.stopTyping(activeRoom.id);
                            }, 1000);
                        }}
                        onKeyDown={handleKeyPress}
                        placeholder="Type a message..."
                        className="w-full pl-4 pr-12 py-3 bg-gray-50 border border-gray-200 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all text-sm"
                    />
                    <button
                        type="submit"
                        disabled={!newMessage.trim()}
                        className="absolute right-2 p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                        <PaperAirplaneIcon className="w-4 h-4" />
                    </button>
                </form>
            </div>
        </div>
    );
};

export default ChatWindow;
